import { useEffect, useState } from "react";

// Weather — Open-Meteo powered, no API key required. Pass either:
//   <Weather lat={39.16} lng={-84.45} />     // direct coordinates (preferred)
//   <Weather zip="45245" />                  // resolves zip→lat/lng via zippopotam.us
// Either path falls back to a Cincinnati default if both are missing so the
// TV screen never paints a broken widget.
//
// Refreshes every 10 minutes. Uses Fahrenheit by default (US dealer base);
// pass `unit="celsius"` to flip. Renders an inline SVG icon matched to
// Open-Meteo's WMO weather code, with the current temperature in Fraunces
// serif and a one-line description.

const FALLBACK = { lat: 39.16, lng: -84.45, label: "Cincinnati, OH" };

// WMO weather codes → label + icon name. Reference:
// https://open-meteo.com/en/docs#weathervariables (table 5)
function decodeWmoCode(code) {
  if (code === 0)                          return { label: "Clear",         icon: "sun" };
  if (code === 1 || code === 2)            return { label: "Partly cloudy", icon: "sun-cloud" };
  if (code === 3)                          return { label: "Overcast",      icon: "cloud" };
  if (code === 45 || code === 48)          return { label: "Foggy",         icon: "fog" };
  if (code >= 51 && code <= 57)            return { label: "Drizzle",       icon: "drizzle" };
  if (code >= 61 && code <= 67)            return { label: "Rain",          icon: "rain" };
  if (code >= 71 && code <= 77)            return { label: "Snow",          icon: "snow" };
  if (code >= 80 && code <= 82)            return { label: "Rain showers",  icon: "rain" };
  if (code === 85 || code === 86)          return { label: "Snow showers",  icon: "snow" };
  if (code >= 95)                          return { label: "Thunderstorm",  icon: "storm" };
  return { label: "—", icon: "cloud" };
}

function WeatherIcon({ name, size = 40, color = "currentColor" }) {
  // SEVRIC house line-icon style: 1.8 stroke, currentColor, viewBox 0 0 24 24.
  const props = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: color, strokeWidth: "1.6",
    strokeLinecap: "round", strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  switch (name) {
    case "sun": return (
      <svg {...props}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
    case "sun-cloud": return (
      <svg {...props}>
        <circle cx="8" cy="9" r="3" />
        <path d="M8 3v1M3 9h1M5.05 5.93l.7.7" />
        <path d="M19 17a3 3 0 0 0-2.83-3.99 5 5 0 0 0-9.81 1.45A3 3 0 0 0 8 21h11a3 3 0 0 0 0-6z" transform="translate(0 -2)" />
      </svg>
    );
    case "cloud": return (
      <svg {...props}>
        <path d="M17.5 19a4.5 4.5 0 0 0 .92-8.91 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 19z" />
      </svg>
    );
    case "fog": return (
      <svg {...props}>
        <path d="M3 8h18M3 12h14M3 16h18M3 20h12" />
      </svg>
    );
    case "drizzle": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M8 18l-1 2M12 18l-1 2M16 18l-1 2" />
      </svg>
    );
    case "rain": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M9 19l-1.5 3M13 19l-1.5 3M17 19l-1.5 3" />
      </svg>
    );
    case "snow": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M8 18v4M12 19v4M16 18v4M7 20h2M11 21h2M15 20h2" />
      </svg>
    );
    case "storm": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M13 14l-3 5h3l-2 4" />
      </svg>
    );
    default: return (
      <svg {...props}>
        <circle cx="12" cy="12" r="6" />
      </svg>
    );
  }
}

export function Weather({
  lat, lng, zip, label,
  unit = "fahrenheit",
  size = "lg", // "lg" | "md" | "sm"
}) {
  const [coords, setCoords] = useState(() => (
    typeof lat === "number" && typeof lng === "number"
      ? { lat, lng, label: label || "" }
      : null
  ));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Resolve ZIP → lat/lng once if no coords supplied. Uses zippopotam.us
  // (free, no API key). Result cached in localStorage so we don't re-resolve
  // every refresh on the showroom TV.
  useEffect(() => {
    if (coords) return;
    if (typeof lat === "number" && typeof lng === "number") {
      setCoords({ lat, lng, label: label || "" });
      return;
    }
    if (!zip) {
      setCoords(FALLBACK);
      return;
    }
    const cacheKey = `sevric.weather.zip.${zip}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.lat && parsed?.lng) {
          setCoords(parsed);
          return;
        }
      }
    } catch { /* localStorage blocked — fall through to fetch */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
        if (!r.ok) throw new Error(`zip lookup failed: ${r.status}`);
        const j = await r.json();
        const place = j?.places?.[0];
        if (!place) throw new Error("no place");
        const c = {
          lat: parseFloat(place.latitude),
          lng: parseFloat(place.longitude),
          label: `${place["place name"]}, ${place["state abbreviation"]}`,
        };
        if (cancelled) return;
        try { localStorage.setItem(cacheKey, JSON.stringify(c)); } catch {}
        setCoords(c);
      } catch {
        // ZIP lookup failed — fall back silently. We deliberately DO NOT
        // set `error` here because the fallback coords still let us load
        // a real forecast. Only the actual weather fetch failing should
        // surface "Weather unavailable".
        if (!cancelled) setCoords(FALLBACK);
      }
    })();
    return () => { cancelled = true; };
  }, [zip, lat, lng, label, coords]);

  // Fetch weather every 10 minutes. Open-Meteo returns current + daily forecast.
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    async function load() {
      try {
        const url = "https://api.open-meteo.com/v1/forecast"
          + `?latitude=${coords.lat}&longitude=${coords.lng}`
          + "&current=temperature_2m,weather_code,wind_speed_10m"
          + "&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max"
          + `&temperature_unit=${unit}`
          + (unit === "fahrenheit" ? "&wind_speed_unit=mph" : "")
          + "&timezone=auto&forecast_days=4";
        const r = await fetch(url);
        if (!r.ok) throw new Error(`weather ${r.status}`);
        const j = await r.json();
        if (!cancelled) { setData(j); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err);
      }
    }
    load();
    const id = setInterval(load, 600_000); // 10 minutes
    return () => { cancelled = true; clearInterval(id); };
  }, [coords?.lat, coords?.lng, unit]);

  // Loading skeleton — single shimmer block sized to the eventual layout.
  if (!data) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)" }}>
          Weather
        </div>
        <div style={{ fontSize: 14, color: "var(--text-dim)" }}>
          {error ? "Weather unavailable" : "Loading…"}
        </div>
      </div>
    );
  }

  const tempNow = Math.round(data.current?.temperature_2m ?? 0);
  const codeNow = data.current?.weather_code ?? 0;
  const wind    = Math.round(data.current?.wind_speed_10m ?? 0);
  const decoded = decodeWmoCode(codeNow);
  const tempUnit = unit === "fahrenheit" ? "°F" : "°C";

  const highToday = Math.round(data.daily?.temperature_2m_max?.[0] ?? 0);
  const lowToday  = Math.round(data.daily?.temperature_2m_min?.[0] ?? 0);
  const tomorrowCode = data.daily?.weather_code?.[1];
  const tomorrowHigh = Math.round(data.daily?.temperature_2m_max?.[1] ?? 0);
  const tomorrowLow  = Math.round(data.daily?.temperature_2m_min?.[1] ?? 0);
  const tomorrowDecoded = tomorrowCode != null ? decodeWmoCode(tomorrowCode) : null;

  if (size === "line") {
    // One-line contextual strip. Used as part of the Today page top bar.
    // Pairs the current temp with tonight's low so the user gets the only
    // two numbers that matter for battery decisions in one glance.
    const tonightLow = lowToday; // open-meteo's daily low for today
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--text-muted)" }}>
        <WeatherIcon name={decoded.icon} size={16} color="var(--accent)" />
        <span style={{ color: "var(--text)", fontWeight: 500 }}>
          {tempNow}<span style={{ opacity: 0.65 }}>{tempUnit}</span>
        </span>
        <span style={{ color: "var(--text-dim)" }}>·</span>
        <span>{decoded.label.toLowerCase()}</span>
        <span style={{ color: "var(--text-dim)" }}>·</span>
        <span>
          tonight <b style={{ color: tonightLow <= 40 ? "var(--accent)" : "var(--text)", fontWeight: 500 }}>{tonightLow}°</b>
        </span>
      </span>
    );
  }

  if (size === "sm") {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--text)" }}>
        <WeatherIcon name={decoded.icon} size={18} color="var(--brand-cobalt)" />
        <span style={{ fontFamily: "'Crimson Pro', serif", fontSize: 20, lineHeight: 1 }}>
          {tempNow}<span style={{ fontSize: 12, opacity: 0.65 }}>{tempUnit}</span>
        </span>
      </div>
    );
  }

  if (size === "md") {
    return (
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 6 }}>
          {coords?.label || "Weather"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <WeatherIcon name={decoded.icon} size={32} color="var(--brand-cobalt)" />
          <div>
            <div style={{ fontFamily: "'Crimson Pro', serif", fontSize: 32, lineHeight: 1, letterSpacing: "-0.03em" }}>
              {tempNow}<span style={{ fontSize: 16, opacity: 0.65 }}>{tempUnit}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {decoded.label} · H {highToday}° L {lowToday}°
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Build the 3-day forward forecast (skip index 0 = today). We always
  // request 4 forecast days from Open-Meteo so we have today + the next 3.
  // Day labels are short weekday names from the local timezone.
  const days = Array.isArray(data.daily?.time) ? data.daily.time : [];
  const forecast = [];
  for (let i = 1; i < days.length && forecast.length < 3; i++) {
    const code = data.daily.weather_code?.[i];
    const hi   = data.daily.temperature_2m_max?.[i];
    const lo   = data.daily.temperature_2m_min?.[i];
    const pop  = data.daily.precipitation_probability_max?.[i];
    if (code == null) continue;
    const dt = new Date(`${days[i]}T12:00:00`);
    const label = i === 1
      ? "Tomorrow"
      : dt.toLocaleDateString(undefined, { weekday: "short" });
    forecast.push({
      key: days[i],
      label,
      decoded: decodeWmoCode(code),
      hi: Math.round(hi ?? 0),
      lo: Math.round(lo ?? 0),
      pop: typeof pop === "number" ? pop : null,
    });
  }

  // Large — current condition + 3-day forecast strip. Used on Home + TvMode.
  return (
    <div>
      <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 8 }}>
        {coords?.label || "Weather"}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <WeatherIcon name={decoded.icon} size={56} color="var(--brand-cobalt)" />
        <div>
          <div style={{ fontFamily: "'Crimson Pro', serif", fontWeight: 400, fontSize: 56, lineHeight: 0.95, letterSpacing: "-0.04em" }}>
            {tempNow}<span style={{ fontSize: 24, opacity: 0.65 }}>{tempUnit}</span>
          </div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 4 }}>
            {decoded.label} · H {highToday}° L {lowToday}° · Wind {wind}{unit === "fahrenheit" ? " mph" : " km/h"}
          </div>
        </div>
      </div>

      {forecast.length > 0 && (
        <div style={{
          marginTop: 14, paddingTop: 12,
          borderTop: "1px solid var(--line)",
          display: "grid",
          gridTemplateColumns: `repeat(${forecast.length}, minmax(0, 1fr))`,
          gap: 10,
        }}>
          {forecast.map((d) => (
            <div key={d.key} style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: "8px 4px",
              borderRadius: 10,
              background: "var(--bg)",
              border: "1px solid var(--line)",
            }}>
              <div style={{
                fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                color: "var(--text-dim)", marginBottom: 6,
              }}>
                {d.label}
              </div>
              <WeatherIcon name={d.decoded.icon} size={28} color="var(--brand-cobalt)" />
              <div style={{
                fontFamily: "'Crimson Pro', serif",
                fontSize: 18, marginTop: 6, letterSpacing: "-0.02em",
                color: "var(--text)",
              }}>
                {d.hi}<span style={{ opacity: 0.55 }}>°</span>
                <span style={{ color: "var(--text-dim)", margin: "0 4px" }}>·</span>
                <span style={{ color: "var(--text-muted)" }}>{d.lo}°</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                {d.decoded.label}{d.pop != null && d.pop >= 20 ? ` · ${d.pop}%` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
